// Authentication service for login/logout operations
import { apiUtils } from '../hooks/useApi';
import { throwApiError } from '../utils';
import {
  API_ENDPOINTS,
  LoginPayload,
  LoginResponse,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  VerifyTokenPayload,
  TabResponse,
  SignUpResponse,
} from './apiEndpoints';
import { serializeUser, parseStoredUser } from '../utils/encryption';

export class AuthService {
  /**
   * Login user with username and password
   */
  static async login(credentials: LoginPayload): Promise<LoginResponse> {
    try {
      const response = await apiUtils.post<LoginResponse>(
        API_ENDPOINTS.auth.login,
        credentials
      );

      return response.data as any;
    } catch (error: any) {
      // Re-throw the original error to preserve response status code
      // This is important for handling special cases like 428 (password change required)
      throw error;
    }
  }

  /**
   * Sign up user with username and password
   */
  static async signup(credentials: {username: string}): Promise<SignUpResponse> {
    try {
      const response = await apiUtils.post<SignUpResponse>(
        API_ENDPOINTS.auth.signup,
        credentials
      );
      return response.data;
    } catch (error: any) {
      // Re-throw the original error to preserve response status code
      throw error;
    }
  }

  /**
   * Logout user
   */
  static async logout(): Promise<void> {
    try {
      await apiUtils.post(API_ENDPOINTS.auth.logout);
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Log out API call failed:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('aloAuthToken');
      localStorage.removeItem('user');
    }
  }

  /**
   * Refresh authentication token
   */
  static async refreshToken(): Promise<{ token: string }> {
    try {
      const response = await apiUtils.post<{ token: string }>(
        API_ENDPOINTS.auth.refresh
      );

      return response.data as any;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Token refresh failed';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get stored authentication token
   */
  static getToken(): string | null {
    return localStorage.getItem('aloAuthToken');
  }

  /**
   * Store authentication token
   */
  static setToken(token: string): void {
    localStorage.setItem('aloAuthToken', token);
  }

  /**
   * Remove authentication token
   */
  static removeToken(): void {
    localStorage.removeItem('aloAuthToken');
  }

  /**
   * Get stored user data.
   *
   * Returns null for absent, legacy-format or unparseable data. Callers must
   * already handle null — AuthContext re-fetches the profile when a token is
   * present without a cached user.
   */
  static async getUser(): Promise<any> {
    const stored = localStorage.getItem('user');
    if (!stored) return null;

    const user = await parseStoredUser(stored);
    if (user === null) {
      // Unusable payload: clear it so the caller's re-fetch path takes over
      // and we do not re-read it on every load.
      this.removeUser();
      return null;
    }

    return user;
  }

  /**
   * Store user data in localStorage.
   */
  static async setUser(user: any): Promise<void> {
    try {
      localStorage.setItem('user', await serializeUser(user));
    } catch (error) {
      console.error('Failed to store user data:', error);
      throw new Error('Failed to store user data');
    }
  }

  /**
   * Remove stored user data
   */
  static removeUser(): void {
    localStorage.removeItem('user');
  }

  /**
   * Check if user is authenticated
   */
  static async isAuthenticated(): Promise<boolean> {
    const token = this.getToken();
    const user = await this.getUser();
    return !!(token && user);
  }

  /**
   * Request password reset email
   */
  static async forgotPassword(username: string): Promise<void> {
    try {
      const payload: ForgotPasswordPayload = { username };
      const response = await apiUtils.post(API_ENDPOINTS.auth.forgotPassword, payload);
      return response.data;
    } catch (error: any) {
      // Handle specific error cases
      if (error.response?.status === 404) {
        throw new Error('No account found with this email address');
      }

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to send confirmation code. Please try again.';
      throw new Error(errorMessage);
    }
  }

  /**
   * Verify reset token
   */
  static async verifyResetToken(token: string): Promise<boolean> {
    try {
      const payload: VerifyTokenPayload = { token };
      await apiUtils.post(API_ENDPOINTS.auth.verifyToken, payload);
      return true;
    } catch (error: any) {
      return false;
    }
  }

  /**
   * Reset password with confirmation code
   */
  static async resetPassword(
    email: string,
    confirmationCode: string,
    newPassword: string
  ): Promise<void> {
    try {
      const code = confirmationCode;
      const payload: ResetPasswordPayload = {
        email,
        code,
        newPassword,
      };
      await apiUtils.post(API_ENDPOINTS.auth.resetPassword, payload);
    } catch (error: any) {
      // Handle specific error cases
      if (error.response?.status === 404) {
        throw new Error('Invalid confirmation code or email address');
      }
      if (error.response?.status === 400) {
        throw new Error(
          error.response?.data?.message || 'Invalid confirmation code'
        );
      }

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to reset password. Please try again.';
      throw new Error(errorMessage);
    }
  }

  /**
   * Resend temporary password to email
   */
  static async resendTemporaryPassword(email: string): Promise<void> {
    try {
      await apiUtils.post(API_ENDPOINTS.auth.resendTemporaryPassword, {
        email,
      });
    }
    catch (error: any) {
      throwApiError(error, 'Failed to resend temporary password');
     }
  }

  /** Fetch permissions based on user
   */
  static async fetchPermissions(): Promise<TabResponse['tabs']> {
    try {
      const response = await apiUtils.get(API_ENDPOINTS.auth.permissions);
      const tabsData = (response.data as any).tabs;
      return tabsData;
    } catch (error: any) {
      throwApiError(error, 'Failed to fetch permissions');
      return [];
    }
  }

  /**
   * Change password for first-time login
   */
  static async changePassword(
    email: string,
    temporaryPassword: string,
    newPassword: string
  ): Promise<void> {
    try {
      await apiUtils.post(API_ENDPOINTS.auth.changePassword, {
        email,
        temporaryPassword,
        newPassword,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to change password';
      throw new Error(errorMessage);
    }
  }

  /**
   * Initiate SSO login with specified provider
   * @param code - The authorization code received from the SSO provider
   * @param provider - The SSO provider identifier (e.g., 'AMAZON', 'GOOGLE')
   * @return The login response containing user data and token
   * @throws Error if the SSO callback fails
   */
  static async ssoCallback(code: string, provider: string): Promise<LoginResponse> { 
    try {
      const response = await apiUtils.post<LoginResponse>(
        API_ENDPOINTS.auth.ssoCallback,
        { code, provider }
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'SSO callback failed');
      return null as any;
    }
  }
}

export default AuthService;
